import React, { useState, useEffect } from 'react';
import { showError, showSuccess } from '../utils/alertas';

const IS_PROD = true;
const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';
const SERVER_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br' : 'http://localhost:3001';

function EdicaoModal({ isOpen, onClose, empreendimento, onUpdateSuccess }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingCapa, setUploadingCapa] = useState(false);

  const [listaGerencias, setListaGerencias] = useState([]);
  const [listaMunicipios, setListaMunicipios] = useState([]); // Lista para o autocomplete

  // -----------------------------------------------------------------------
  // 🔄 CARGA DE DADOS
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (isOpen) {
      carregarListasAuxiliares();
    }

    if (empreendimento && isOpen) {
      console.log("🛠️ [MODAL] Dados carregados:", empreendimento);

      setFormData({
        // IDs
        id: empreendimento.id,
        status_id: empreendimento.status_id || '',
        gerencia_id: empreendimento.gerencia_id || '',
        numero_contrato_principal: empreendimento.numero_contrato_principal || '',

        // Textos Básicos
        nome: empreendimento.nome || '',

        // MUNICÍPIO EDITÁVEL (Salvamos o NOME, pois é o que o backend espera no momento)
        nome_municipio: empreendimento.nome_municipio || '',

        descricao_curta: empreendimento.descricao_curta || '',
        descricao_completa: empreendimento.descricao_completa || '',
        historia_narrativa: empreendimento.historia_narrativa || '',
        imagem_capa_url: empreendimento.imagem_capa_url || '',

        // Localização
        endereco: empreendimento.endereco || '',
        latitude: empreendimento.latitude || '',
        longitude: empreendimento.longitude || '',

        // Dados Técnicos
        corpo_hidrico: empreendimento.corpo_hidrico || '',
        classe_rio: empreendimento.classe_rio || '',
        populacao_atendida: empreendimento.populacao_atendida || '',
        vazao_atual_ls: empreendimento.vazao_atual_ls || '',
        vazao_futura_ls: empreendimento.vazao_futura_ls || '',

        // Controles (Padrão 'NI' se vazio)
        controle_vazao_entrada: empreendimento.controle_vazao_entrada || 'NI',
        controle_vazao_saida: empreendimento.controle_vazao_saida || 'NI',

        // Textos Longos
        texto_beneficios: empreendimento.texto_beneficios || empreendimento.beneficios || '',
        texto_diferenciais: empreendimento.texto_diferenciais || empreendimento.diferenciais || '',
        texto_sustentabilidade: empreendimento.texto_sustentabilidade || empreendimento.sustentabilidade || ''
      });
    }
  }, [empreendimento, isOpen]);

  const carregarListasAuxiliares = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Busca Gerencias e Municípios em paralelo
      const [resGer, resMun] = await Promise.all([
        fetch(`${API_BASE_URL}/gerencias`, { headers }),
        fetch(`${API_BASE_URL}/municipios`, { headers }) // Tenta buscar municípios
      ]);

      if (resGer.ok) setListaGerencias(await resGer.json());

      // Se a rota de municípios existir e retornar dados, preenche a lista
      if (resMun.ok) {
        const muns = await resMun.json();
        console.log("🔥 Resposta Municípios:", muns); // LOG PARA DEBUG
        setListaMunicipios(muns);
      } else {
        console.error("❌ Falha ao carregar municípios:", resMun.status);
      }

    } catch (error) { console.error("Erro listas:", error); }
  };

  if (!isOpen) return null;

  // -----------------------------------------------------------------------
  // 🎮 HANDLERS
  // -----------------------------------------------------------------------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCapaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCapa(true);
    const token = localStorage.getItem('token');
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formDataUpload
      });
      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, imagem_capa_url: data.url }));
        showSuccess("Upload", "Capa atualizada.");
      } else { showError("Erro", "Falha no upload."); }
    } catch (error) { showError("Erro", "Erro de conexão."); }
    finally { setUploadingCapa(false); }
  };

  // 🛡️ SUBMIT COM PROTEÇÃO NUMÉRICA INTELIGENTE
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('token');

    // Função de limpeza numérica corrigida
    const cleanNumber = (val) => {
      if (val === null || val === undefined || val === '') return null;
      if (typeof val === 'number') return val;
      const strVal = String(val).trim();
      // Se tiver vírgula, assume formato BR (1.200,50 -> 1200.50)
      if (strVal.includes(',')) {
        return parseFloat(strVal.replace(/\./g, '').replace(',', '.'));
      }
      return parseFloat(strVal);
    };

    // Função para limpar coordenadas
    const cleanCoord = (val) => {
      if (!val) return null;
      const strVal = String(val).replace(',', '.');
      const num = parseFloat(strVal);
      return isNaN(num) ? null : num;
    }

    const payload = {
      ...formData,
      // IDs: Vazio vira NULL
      status_id: (!formData.status_id || formData.status_id === '') ? null : parseInt(formData.status_id),
      gerencia_id: (!formData.gerencia_id || formData.gerencia_id === '') ? null : parseInt(formData.gerencia_id),

      // Numéricos: Limpeza segura
      vazao_atual_ls: cleanNumber(formData.vazao_atual_ls),
      vazao_futura_ls: cleanNumber(formData.vazao_futura_ls),
      populacao_atendida: cleanNumber(formData.populacao_atendida),

      // Coordenadas
      latitude: cleanCoord(formData.latitude),
      longitude: cleanCoord(formData.longitude),

      // Controles: Garante texto válido ou 'NI'
      controle_vazao_entrada: formData.controle_vazao_entrada || 'NI',
      controle_vazao_saida: formData.controle_vazao_saida || 'NI',

      // Município: Envia o nome digitado/selecionado
      nome_municipio: formData.nome_municipio
    };

    console.log("📤 [EDIÇÃO] Enviando Payload:", payload);

    try {
      const response = await fetch(`${API_BASE_URL}/empreendimentos/${empreendimento.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await showSuccess("Sucesso!", "Dados atualizados.");
        onUpdateSuccess();
        onClose();
      } else {
        const err = await response.json();
        console.error("❌ Erro Backend:", err);
        showError("Erro ao Salvar", err.message || "Verifique os dados.");
      }
    } catch (error) {
      console.error(error);
      showError("Erro", "Erro de conexão.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col border border-white/20">

        {/* HEADER */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-slate-50 shrink-0">
          <div>
            <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
              <span className="text-2xl">🏗️</span> Edição Completa
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
              {formData.nome || 'Nova Obra'}
            </p>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-red-50 hover:text-red-500 text-slate-400 transition-all text-xl font-bold">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-8 overflow-y-auto custom-scrollbar bg-white">

          {/* 1. IDENTIDADE E LOCALIZAÇÃO */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-black text-sabesp-blue uppercase tracking-[0.2em] border-l-4 border-sabesp-blue pl-3">Identidade & Local</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">

              {/* Capa */}
              <div className="md:col-span-1 bg-slate-50 p-4 rounded-2xl border-2 border-dashed border-slate-200">
                <label className="label-form">Capa do Card</label>
                <div className="flex items-center gap-4 mt-2">
                  <div className="w-14 h-14 bg-white rounded-xl border overflow-hidden shrink-0 shadow-sm relative group">
                    {formData.imagem_capa_url ? (
                      <img src={formData.imagem_capa_url.startsWith('http') ? formData.imagem_capa_url : `${SERVER_URL}${formData.imagem_capa_url}`} alt="Capa" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">Sem img</div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input type="file" accept="image/*" onChange={handleCapaUpload} className="hidden" id="capa-upload" />
                    <label htmlFor="capa-upload" className="text-[10px] bg-white border border-slate-200 text-slate-600 px-3 py-2 rounded-lg font-bold cursor-pointer hover:bg-slate-100 block text-center shadow-sm">
                      {uploadingCapa ? '...' : '📷 Alterar'}
                    </label>
                  </div>
                </div>
              </div>

              {/* Dados Principais */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-form">Nome da ETE / Obra</label>
                  <input name="nome" value={formData.nome || ''} onChange={handleChange} className="input-form font-bold text-slate-700" />
                </div>
                <div>
                  <label className="label-form">Gerência Responsável</label>
                  <select name="gerencia_id" value={formData.gerencia_id || ''} onChange={handleChange} className="input-form cursor-pointer">
                    <option value="">Selecione...</option>
                    {listaGerencias.map(g => (
                      <option key={g.id} value={g.id}>{g.nome}</option>
                    ))}
                  </select>
                </div>

                {/* Localização Completa */}
                <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-3">
                    <label className="label-form">Endereço Completo</label>
                    <input name="endereco" value={formData.endereco || ''} onChange={handleChange} className="input-form" placeholder="Rua, Número, Bairro, Cidade..." />
                  </div>

                  {/* MUNICÍPIO COM BUSCA (DATALIST) */}
                  <div>
                    <label className="label-form">Município</label>

                    {/* Input de Texto conectado ao Datalist */}
                    <input
                      list="lista-municipios-sp"
                      name="nome_municipio"
                      value={formData.nome_municipio || ''}
                      onChange={handleChange}
                      className="input-form font-bold text-slate-700 placeholder:font-normal"
                      placeholder={listaMunicipios.length > 0 ? "Pesquise o município..." : "Carregando cidades..."}
                      autoComplete="off"
                      disabled={loading} // Opcional: desabilita enquanto salva, mas mantém ativo na carga
                    />

                    {/* Datalist (Sempre renderizado para garantir que o input o encontre) */}
                    <datalist id="lista-municipios-sp">
                      {listaMunicipios.map((mun) => (
                        // A key deve ser única. O value é o que vai para o input ao clicar.
                        <option key={mun.id} value={mun.nome}>
                          {/* O texto dentro da tag option é secundário em alguns browsers, mas útil */}
                          {mun.regiao_adm ? `${mun.regiao_adm}` : ''}
                        </option>
                      ))}
                    </datalist>

                    {/* DEBUG VISUAL TEMPORÁRIO (Pode remover depois) */}
                    <div className="text-[10px] text-gray-400 mt-1">
                      {listaMunicipios.length === 0
                        ? "⚠️ Nenhuma cidade carregada. Verifique a API."
                        : `✅ ${listaMunicipios.length} cidades disponíveis.`}
                    </div>
                  </div>

                  <div>
                    <label className="label-form">Latitude</label>
                    <input name="latitude" value={formData.latitude || ''} onChange={handleChange} className="input-form font-mono text-xs" placeholder="-23.xxxx" />
                  </div>
                  <div>
                    <label className="label-form">Longitude</label>
                    <input name="longitude" value={formData.longitude || ''} onChange={handleChange} className="input-form font-mono text-xs" placeholder="-46.xxxx" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 2. ENGENHARIA */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] border-l-4 border-slate-200 pl-3">Engenharia & Processos</h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100">
              <div>
                <label className="label-form text-slate-500">Vazão Atual</label>
                <input name="vazao_atual_ls" value={formData.vazao_atual_ls || ''} onChange={handleChange} className="input-form bg-white" placeholder="L/s" />
              </div>
              <div>
                <label className="label-form text-slate-500">Vazão Futura</label>
                <input name="vazao_futura_ls" value={formData.vazao_futura_ls || ''} onChange={handleChange} className="input-form bg-white" placeholder="L/s" />
              </div>
              <div className="col-span-2 md:col-span-2">
                <label className="label-form text-slate-500">População Atendida</label>
                <input name="populacao_atendida" value={formData.populacao_atendida || ''} onChange={handleChange} className="input-form bg-white" placeholder="Habitantes" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* UX MELHORADA: Mostra texto amigável para o usuário */}
              <div>
                <label className="label-form">Controle Entrada</label>
                <select name="controle_vazao_entrada" value={formData.controle_vazao_entrada || 'NI'} onChange={handleChange} className="input-form cursor-pointer">
                  <option value="NI">Não Informado</option>
                  <option value="Sim">Sim</option>
                  <option value="Não">Não</option>
                </select>
              </div>
              <div>
                <label className="label-form">Controle Saída</label>
                <select name="controle_vazao_saida" value={formData.controle_vazao_saida || 'NI'} onChange={handleChange} className="input-form cursor-pointer">
                  <option value="NI">Não Informado</option>
                  <option value="Sim">Sim</option>
                  <option value="Não">Não</option>
                </select>
              </div>
              <div>
                <label className="label-form">Corpo Hídrico</label>
                <input name="corpo_hidrico" value={formData.corpo_hidrico || ''} onChange={handleChange} className="input-form" />
              </div>
              <div>
                <label className="label-form">Classe do Rio</label>
                <input name="classe_rio" value={formData.classe_rio || ''} onChange={handleChange} className="input-form" />
              </div>
            </div>
          </section>

          {/* 3. TEXTOS */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] border-l-4 border-slate-200 pl-3">Destaques e Benefícios</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-form">Diferenciais Técnicos (Tópicos)</label>
                <textarea name="texto_diferenciais" value={formData.texto_diferenciais || ''} onChange={handleChange} className="input-form h-28 resize-none leading-relaxed" placeholder="- Tecnologia&#10;- Eficiência" />
              </div>
              <div>
                <label className="label-form">Benefícios para a População</label>
                <textarea name="texto_beneficios" value={formData.texto_beneficios || ''} onChange={handleChange} className="input-form h-28 resize-none leading-relaxed" placeholder="- Saúde&#10;- Meio Ambiente" />
              </div>
            </div>
          </section>

          {/* 4. INSTITUCIONAL */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] border-l-4 border-slate-200 pl-3">Conteúdo Institucional</h3>
            <div className="grid grid-cols-1 gap-4">
              <div><label className="label-form">DEscrição Curta (Abaixo do título principal)</label><textarea name="descricao_curta" value={formData.descricao_curta || ''} onChange={handleChange} className="input-form h-20 resize-none" /></div>
              <div><label className="label-form">Concepção de Tratamento</label><textarea name="historia_narrativa" value={formData.historia_narrativa || ''} onChange={handleChange} className="input-form h-32 resize-none" /></div>
            </div>
          </section>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 shrink-0">
            <button type="button" onClick={onClose} className="px-6 py-3 text-slate-400 font-bold hover:text-slate-600 transition-colors uppercase text-[10px] tracking-widest">Cancelar</button>
            <button type="submit" disabled={loading || uploadingCapa} className="px-10 py-3 bg-slate-900 text-white font-black rounded-2xl hover:bg-sabesp-blue transition-all shadow-xl disabled:opacity-50 flex items-center gap-2 transform hover:-translate-y-1">
              {loading ? 'Salvando...' : '💾 Salvar Alterações'}
            </button>
          </div>

        </form>
      </div>

      <style>{`
        .label-form { display: block; font-size: 0.65rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.4rem; margin-left: 0.2rem; }
        .input-form { width: 100%; padding: 0.7rem 1rem; background-color: #f8fafc; border: 2px solid #f1f5f9; border-radius: 0.75rem; font-size: 0.8rem; color: #334155; font-weight: 600; outline: none; transition: all 0.2s; }
        .input-form:focus { border-color: #00AEEF; background-color: #fff; box-shadow: 0 4px 12px -2px rgba(0, 174, 239, 0.15); color: #0f172a; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>
    </div>
  );
}

export default EdicaoModal;